import { SignedIn, SignedOut, useUser } from '@clerk/clerk-react'
import { Box } from '@mui/material'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ProjectsPage } from '@/pages/ProjectsPage'
import { WorkspacePage } from '@/pages/workspace/WorkspacePage'
import { Header } from '@/components/layout/Header'
import { FeedbackWidget } from '@/components/feedback/FeedbackWidget'
import { OrganizationTokenSync } from '@/components/auth/OrganizationTokenSync'
import { LandingPage } from '@/components/landing/LandingPage'
import { useState, useCallback, useMemo } from 'react'

//Hi

// Wrapper component that handles auth integration
function LandingPageWithAuth() {
  return (
    <LandingPage 
      onDemoRequest={() => {
        window.location.href = 'mailto:cx@erdosmiller.com?subject=Wave Demo Request&body=Hi, I\'m interested in getting access to Wave for drilling data visualization. Please let me know next steps.'
      }}
      contactEmail="cx@erdosmiller.com"
    />
  )
}

function App() {
  const { user, isLoaded } = useUser()
  
  // Extract stable values to minimize re-renders on user object changes
  const isSignedIn = useMemo(() => isLoaded && !!user, [isLoaded, user?.id])
  
  // Safely get location with fallback for initial render timing issues
  let location;
  let isInWorkspace = false;
  
  try {
    location = useLocation()
    isInWorkspace = location.pathname.startsWith('/workspace')
  } catch (error) {
    // Router context not ready yet - use defaults
    console.warn('🔄 Router context not ready, using defaults')
    location = { pathname: '/' }
    isInWorkspace = false
  }
  
  const [projectName, setProjectName] = useState<string | null>(null)
  
  // Map Clerk user data to FeatureBase format - only update when essential user props change
  const featureBaseUserData = useMemo(() => {
    // Only return user data if Clerk is loaded and user is authenticated
    if (!isSignedIn || !user) {
      return undefined
    }

    // Extract email (Clerk users can have multiple email addresses)
    const primaryEmail = user.emailAddresses?.find(email => email.id === user.primaryEmailAddressId)?.emailAddress
    
    // Construct full name from available name fields
    const fullName = user.fullName || 
                    (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName) ||
                    primaryEmail?.split('@')[0] || // fallback to email username
                    'Unknown User'

    return {
      mail: primaryEmail,
      id: user.id,
      name: fullName,
      img: user.imageUrl || undefined
    }
  }, [isSignedIn, user?.id, user?.fullName, user?.firstName, user?.lastName, user?.imageUrl, user?.primaryEmailAddressId])
  

  
  // Handle project name updates from workspace
  const handleProjectNameChange = useCallback((name: string | null) => {
    setProjectName(name)
  }, [])

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Monitor organization switches and force token refresh */}
      <SignedIn>
        <OrganizationTokenSync />
      </SignedIn>
      
      {/* Header shown for both signed-in and signed-out users */}
      <Header 
        projectName={projectName || undefined}
        isSignedIn={isSignedIn}
      />

      <SignedOut>
        <LandingPageWithAuth />
      </SignedOut>

      <SignedIn>
        <Box sx={{ 
          pt: isInWorkspace ? { xs: '56px', sm: '64px' } : { xs: 'calc(56px + 24px)', sm: 'calc(64px + 24px)' }, // Account for fixed header
          pb: isInWorkspace ? 0 : 3,
          px: isInWorkspace ? 0 : 3 
        }}>
          <Routes>
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/workspace/:projectId" element={<WorkspacePage onProjectNameChange={handleProjectNameChange} />} />
          </Routes>
        </Box>
        
        {/* FeatureBase Feedback Widget - available for all signed-in users */}
        <FeedbackWidget 
          showButton={true} 
          user={featureBaseUserData}
        />
      </SignedIn>
    </Box>
  )
}

export default App
